'use client';

/**
 * Workflow Instance Detail Component
 *
 * Displays detailed information about a specific workflow instance including:
 * - Status and actions (Pause, Cancel, Create Referral)
 * - Step-by-step progress tracker
 * - Member response history
 * - Linked referral panel
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { TypedStepCard } from '@/components/workflows/TypedStepCard';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  ArrowLeft,
  Play,
  Pause,
  XCircle,
  CheckCircle,
  Clock,
  AlertTriangle,
  Users,
  Zap,
  Bot,
  Heart,
  UserCheck,
  Calendar,
  Activity,
  FileText,
  ExternalLink,
  Loader2,
  RefreshCw,
  Plus,
  MessageSquare,
  Phone,
  Mail,
  Send,
  ChevronRight,
  User,
  Clipboard,
  SkipForward,
  CircleDot,
} from 'lucide-react';
import {
  tcoWorkflowsAPI,
  WorkflowInstance,
  WorkflowTemplate,
  WorkflowStatus,
  WorkflowOutcome,
  StepExecution,
  StepExecutionStatus,
} from '@/services/api/tcoServiceReferrals';
import { useToast } from '@/components/ui/use-toast';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface WorkflowInstanceDetailProps {
  instanceId: number;
  onBack?: () => void;
  onViewReferral?: (referralId: number) => void;
  className?: string;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const getStatusIcon = (status: WorkflowStatus) => {
  switch (status) {
    case 'pending':
      return <Clock className="w-5 h-5 text-gray-500" />;
    case 'queued':
      return <Clock className="w-5 h-5 text-blue-500" />;
    case 'in_progress':
      return <Play className="w-5 h-5 text-blue-600" />;
    case 'waiting_response':
      return <Clock className="w-5 h-5 text-amber-500" />;
    case 'paused':
      return <Pause className="w-5 h-5 text-gray-500" />;
    case 'completed':
      return <CheckCircle className="w-5 h-5 text-green-600" />;
    case 'cancelled':
      return <XCircle className="w-5 h-5 text-gray-500" />;
    case 'failed':
      return <AlertTriangle className="w-5 h-5 text-red-500" />;
    default:
      return <Activity className="w-5 h-5 text-gray-400" />;
  }
};

const getStatusColor = (status: WorkflowStatus) => {
  switch (status) {
    case 'pending':
      return 'bg-gray-100 text-gray-700 border-gray-200';
    case 'queued':
      return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'in_progress':
      return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'waiting_response':
      return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'paused':
      return 'bg-gray-100 text-gray-600 border-gray-200';
    case 'completed':
      return 'bg-green-100 text-green-700 border-green-200';
    case 'cancelled':
      return 'bg-gray-100 text-gray-600 border-gray-200';
    case 'failed':
      return 'bg-red-100 text-red-700 border-red-200';
    default:
      return 'bg-gray-100 text-gray-600 border-gray-200';
  }
};

const getOutcomeColor = (outcome?: WorkflowOutcome) => {
  switch (outcome) {
    case 'success':
    case 'member_engaged':
    case 'referral_created':
      return 'bg-green-100 text-green-700';
    case 'member_declined':
    case 'no_response':
      return 'bg-amber-100 text-amber-700';
    case 'escalated':
    case 'failed':
    case 'cancelled_by_user':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

const getTriggerIcon = (triggerType: string) => {
  switch (triggerType) {
    case 'sdoh_screening':
      return <Heart className="w-4 h-4" />;
    case 'risk_score':
      return <AlertTriangle className="w-4 h-4" />;
    case 'care_plan_goal':
      return <Clipboard className="w-4 h-4" />;
    case 'enrollment_event':
      return <UserCheck className="w-4 h-4" />;
    case 'scheduled':
      return <Calendar className="w-4 h-4" />;
    case 'manual':
      return <Users className="w-4 h-4" />;
    default:
      return <Zap className="w-4 h-4" />;
  }
};

// formatDate is now inside the component using useFormattedDate hook

const getPriorityLabel = (priority: number) => {
  switch (priority) {
    case 1:
      return 'Low';
    case 2:
      return 'Medium';
    case 3:
      return 'High';
    case 4:
      return 'Urgent';
    default:
      return `Priority ${priority}`;
  }
};

const getPriorityColor = (priority: number) => {
  switch (priority) {
    case 1:
      return 'bg-gray-100 text-gray-600';
    case 2:
      return 'bg-blue-100 text-blue-600';
    case 3:
      return 'bg-orange-100 text-orange-600';
    case 4:
      return 'bg-red-100 text-red-600';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

// ============================================================================
// COMPONENT
// ============================================================================

export function WorkflowInstanceDetail({
  instanceId,
  onBack,
  onViewReferral,
  className = '',
}: WorkflowInstanceDetailProps) {
  const { toast } = useToast();
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    return fmt.dateTime(dateStr);
  };

  // State
  const [loading, setLoading] = useState(true);
  const [instance, setInstance] = useState<WorkflowInstance | null>(null);
  const [template, setTemplate] = useState<WorkflowTemplate | null>(null);
  const [hasError, setHasError] = useState(false);

  // Action dialogs
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showRecordResponseDialog, setShowRecordResponseDialog] = useState(false);
  const [showCreateReferralDialog, setShowCreateReferralDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Action form state
  const [cancelReason, setCancelReason] = useState('');
  const [responseType, setResponseType] = useState('positive');
  const [responseContent, setResponseContent] = useState('');
  const [referralServiceTypes, setReferralServiceTypes] = useState<string[]>([]);
  const [referralUrgency, setReferralUrgency] = useState('routine');
  const [referralNotes, setReferralNotes] = useState('');

  // Step action state
  const [showStepActionDialog, setShowStepActionDialog] = useState(false);
  const [selectedStepNumber, setSelectedStepNumber] = useState<number | null>(null);
  const [selectedStepAction, setSelectedStepAction] = useState<'start' | 'complete' | 'skip' | 'fail'>('start');
  const [stepActionNotes, setStepActionNotes] = useState('');
  const [startingWorkflow, setStartingWorkflow] = useState(false);

  // Load instance data
  const loadInstance = useCallback(async () => {
    setLoading(true);
    setHasError(false);
    try {
      const instanceData = await tcoWorkflowsAPI.getInstance(instanceId);
      setInstance(instanceData);

      // Load template if we have template_id
      if (instanceData.template_id) {
        try {
          const templateData = await tcoWorkflowsAPI.getTemplate(instanceData.template_id);
          setTemplate(templateData);
        } catch (templateErr) {
          console.warn('Could not load template:', templateErr);
        }
      }
    } catch (err) {
      console.error('Failed to load workflow instance:', err);
      setHasError(true);
      toast({
        title: t('tcoReferrals.detail.error', 'Error'),
        description: t('tcoReferrals.detail.failedToLoad', 'Failed to load workflow instance'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [instanceId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadInstance();
  }, [loadInstance]);

  // Handle cancel workflow
  const handleCancel = async () => {
    setActionLoading(true);
    try {
      await tcoWorkflowsAPI.cancelInstance(instanceId, cancelReason || undefined);
      toast({
        title: t('tcoReferrals.detail.workflowCancelled', 'Workflow Cancelled'),
        description: t('tcoReferrals.detail.cancelledSuccessfully', 'The workflow has been cancelled successfully.'),
      });
      setShowCancelDialog(false);
      setCancelReason('');
      loadInstance();
    } catch (err: any) {
      toast({
        title: t('tcoReferrals.detail.error', 'Error'),
        description: err.response?.data?.detail || t('tcoReferrals.detail.failedToCancel', 'Failed to cancel workflow'),
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Handle record response
  const handleRecordResponse = async () => {
    setActionLoading(true);
    try {
      await tcoWorkflowsAPI.recordResponse(instanceId, {
        response: responseType,
        response_content: responseContent || undefined,
        response_sentiment: responseType === 'positive' ? 'positive' : responseType === 'negative' ? 'negative' : 'neutral',
      });
      toast({
        title: t('tcoReferrals.detail.responseRecorded', 'Response Recorded'),
        description: t('tcoReferrals.detail.responseRecordedDesc', 'Member response has been recorded.'),
      });
      setShowRecordResponseDialog(false);
      setResponseType('positive');
      setResponseContent('');
      loadInstance();
    } catch (err: any) {
      toast({
        title: t('tcoReferrals.detail.error', 'Error'),
        description: err.response?.data?.detail || t('tcoReferrals.detail.failedToRecord', 'Failed to record response'),
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Handle create referral
  const handleCreateReferral = async () => {
    if (referralServiceTypes.length === 0) {
      toast({
        title: t('tcoReferrals.detail.validationError', 'Validation Error'),
        description: t('tcoReferrals.detail.selectServiceType', 'Please select at least one service type'),
        variant: 'destructive',
      });
      return;
    }

    setActionLoading(true);
    try {
      const result = await tcoWorkflowsAPI.createReferralFromWorkflow(instanceId, {
        service_types: referralServiceTypes,
        urgency: referralUrgency,
        notes: referralNotes || undefined,
      });
      toast({
        title: t('tcoReferrals.detail.referralCreated', 'Referral Created'),
        description: t('tcoReferrals.detail.referralCreatedDesc', 'Referral #{{number}} has been created.', { number: result.referral_number }),
      });
      setShowCreateReferralDialog(false);
      setReferralServiceTypes([]);
      setReferralUrgency('routine');
      setReferralNotes('');
      loadInstance();

      // Navigate to the new referral if callback provided
      if (onViewReferral && result.referral_id) {
        onViewReferral(result.referral_id);
      }
    } catch (err: any) {
      toast({
        title: t('tcoReferrals.detail.error', 'Error'),
        description: err.response?.data?.detail || t('tcoReferrals.detail.failedToCreateReferral', 'Failed to create referral'),
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Handle start workflow (transitions pending → in_progress and creates step executions)
  const handleStartWorkflow = async () => {
    setStartingWorkflow(true);
    try {
      await tcoWorkflowsAPI.startWorkflow(instanceId);
      toast({
        title: t('tcoReferrals.detail.workflowStarted', 'Workflow Started'),
        description: t('tcoReferrals.detail.workflowStartedDesc', 'The workflow is now in progress. You can execute each step.'),
      });
      loadInstance();
    } catch (err: any) {
      toast({
        title: t('tcoReferrals.detail.error', 'Error'),
        description: err.response?.data?.detail || t('tcoReferrals.detail.failedToStart', 'Failed to start workflow'),
        variant: 'destructive',
      });
    } finally {
      setStartingWorkflow(false);
    }
  };

  // Handle step action (start, complete, skip, fail)
  const handleStepAction = async () => {
    if (selectedStepNumber === null) return;
    setActionLoading(true);
    try {
      await tcoWorkflowsAPI.executeStep(instanceId, selectedStepNumber, {
        action: selectedStepAction,
        notes: stepActionNotes || undefined,
      });
      toast({
        title: t('tcoReferrals.detail.stepActionSuccess', 'Step Updated'),
        description: `Step ${selectedStepNumber} ${selectedStepAction === 'start' ? 'started' : selectedStepAction === 'complete' ? 'completed' : selectedStepAction === 'skip' ? 'skipped' : 'failed'}.`,
      });
      setShowStepActionDialog(false);
      setStepActionNotes('');
      loadInstance();
    } catch (err: any) {
      toast({
        title: t('tcoReferrals.detail.error', 'Error'),
        description: err.response?.data?.detail || t('tcoReferrals.detail.stepActionFailed', 'Failed to execute step action'),
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const openStepAction = (stepNumber: number, action: 'start' | 'complete' | 'skip' | 'fail') => {
    setSelectedStepNumber(stepNumber);
    setSelectedStepAction(action);
    setStepActionNotes('');
    setShowStepActionDialog(true);
  };

  // Determine if actions are available
  const canCancel = instance && ['pending', 'queued', 'in_progress', 'waiting_response', 'paused'].includes(instance.status);
  const canRecordResponse = instance && ['in_progress', 'waiting_response'].includes(instance.status);
  const canCreateReferral = instance && !instance.service_referral_id && ['in_progress', 'waiting_response', 'completed'].includes(instance.status);
  const canStartWorkflow = instance && ['pending', 'queued'].includes(instance.status);
  const isWorkflowActive = instance && ['in_progress', 'waiting_response'].includes(instance.status);

  if (loading) {
    return (
      <div className={`flex items-center justify-center py-24 ${className}`}>
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (hasError || !instance) {
    return (
      <div className={`flex flex-col items-center justify-center py-24 ${className}`}>
        <AlertTriangle className="w-12 h-12 text-red-400 mb-4" />
        <p className="text-gray-500 mb-4">{t('tcoReferrals.detail.failedToLoadInstance', 'Failed to load workflow instance')}</p>
        <div className="flex gap-2">
          {onBack && (
            <Button variant="outline" onClick={onBack}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('tcoReferrals.detail.goBack', 'Go Back')}
            </Button>
          )}
          <Button onClick={loadInstance}>
            <RefreshCw className="w-4 h-4 mr-2" />
            {t('tcoReferrals.detail.retry', 'Retry')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          {onBack && (
            <Button variant="ghost" size="sm" onClick={onBack} className="mb-2 -ml-2">
              <ArrowLeft className="w-4 h-4 mr-1" />
              {t('tcoReferrals.detail.back', 'Back')}
            </Button>
          )}
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${getStatusColor(instance.status)}`}>
              {getStatusIcon(instance.status)}
            </div>
            <div>
              <h1 className="text-xl font-semibold">
                {instance.template_name || `Workflow #${instance.id}`}
              </h1>
              <p className="text-sm text-gray-500">
                {t('tcoReferrals.detail.instance', 'Instance')} #{instance.id} • {t('tcoReferrals.detail.started', 'Started')} {formatDate(instance.created_at)}
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={loadInstance}>
            <RefreshCw className="w-4 h-4 mr-1" />
            {t('tcoReferrals.detail.refresh', 'Refresh')}
          </Button>
          {canRecordResponse && (
            <Button variant="outline" size="sm" onClick={() => setShowRecordResponseDialog(true)}>
              <MessageSquare className="w-4 h-4 mr-1" />
              {t('tcoReferrals.detail.recordResponse', 'Record Response')}
            </Button>
          )}
          {canCreateReferral && (
            <Button size="sm" onClick={() => setShowCreateReferralDialog(true)}>
              <Plus className="w-4 h-4 mr-1" />
              {t('tcoReferrals.detail.createReferral', 'Create Referral')}
            </Button>
          )}
          {canCancel && (
            <Button variant="destructive" size="sm" onClick={() => setShowCancelDialog(true)}>
              <XCircle className="w-4 h-4 mr-1" />
              {t('tcoReferrals.detail.cancel', 'Cancel')}
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Start Workflow Banner (for pending workflows) */}
          {canStartWorkflow && (
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="py-6">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-blue-100">
                      <Play className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-blue-900">
                        {t('tcoReferrals.detail.readyToStart', 'Workflow Ready to Start')}
                      </p>
                      <p className="text-sm text-blue-700">
                        {t('tcoReferrals.detail.readyToStartDesc', 'This workflow has {{count}} steps. Click Start to begin.', { count: instance.total_steps })}
                      </p>
                    </div>
                  </div>
                  <Button onClick={handleStartWorkflow} disabled={startingWorkflow}>
                    {startingWorkflow ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                    {t('tcoReferrals.detail.startWorkflow', 'Start Workflow')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Progress Tracker */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="w-4 h-4" />
                {t('tcoReferrals.detail.progress', 'Workflow Steps')}
                <span className="text-sm font-normal text-gray-500">
                  ({instance.current_step}/{instance.total_steps})
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Step Cards */}
              <div className="space-y-3">
                {(template?.workflow_steps || []).map((step, index) => {
                  const stepNumber = index + 1;
                  // Find matching step execution from instance data
                  const stepExec = instance.step_executions?.find(
                    (se) => se.step_number === stepNumber
                  );
                  const stepStatus: StepExecutionStatus = stepExec?.status || 'pending';

                  const getStepStatusIcon = () => {
                    switch (stepStatus) {
                      case 'completed':
                        return <CheckCircle className="w-5 h-5 text-green-500" />;
                      case 'executing':
                        return <Play className="w-5 h-5 text-blue-500" />;
                      case 'waiting':
                        return <Clock className="w-5 h-5 text-amber-500" />;
                      case 'failed':
                        return <AlertTriangle className="w-5 h-5 text-red-500" />;
                      case 'skipped':
                        return <SkipForward className="w-5 h-5 text-gray-400" />;
                      default:
                        return <CircleDot className="w-5 h-5 text-gray-300" />;
                    }
                  };

                  const getStepBorder = () => {
                    switch (stepStatus) {
                      case 'completed':
                        return 'border-green-200 bg-green-50/50';
                      case 'executing':
                        return 'border-blue-200 bg-blue-50/50';
                      case 'waiting':
                        return 'border-amber-200 bg-amber-50/50';
                      case 'failed':
                        return 'border-red-200 bg-red-50/50';
                      case 'skipped':
                        return 'border-gray-200 bg-gray-50/50';
                      default:
                        return 'border-gray-100';
                    }
                  };

                  const canStartStep = isWorkflowActive && stepStatus === 'pending';
                  const canCompleteStep = isWorkflowActive && stepStatus === 'executing';
                  const canSkipStep = isWorkflowActive && ['pending', 'executing'].includes(stepStatus);

                  return (
                    <div
                      key={index}
                      className={`flex items-start gap-3 p-3 rounded-lg border ${getStepBorder()} transition-colors`}
                    >
                      <div className="mt-0.5 flex-shrink-0">
                        {getStepStatusIcon()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <TypedStepCard
                          step={step as any}
                          memberId={instance.member_id}
                        />
                        <div className="mt-2 flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {step.step_type ? step.step_type.replace(/_/g, ' ') : 'manual'}
                          </Badge>
                          <Badge
                            variant="outline"
                            className={`text-xs ${
                              stepStatus === 'completed'
                                ? 'text-green-600 border-green-300'
                                : stepStatus === 'executing'
                                ? 'text-blue-600 border-blue-300'
                                : stepStatus === 'failed'
                                ? 'text-red-600 border-red-300'
                                : stepStatus === 'skipped'
                                ? 'text-gray-500 border-gray-300'
                                : 'text-gray-400 border-gray-200'
                            }`}
                          >
                            {stepStatus}
                          </Badge>
                        </div>
                        {stepExec?.started_at && (
                          <p className="text-xs text-gray-500 mt-1">
                            {t('tcoReferrals.detail.started', 'Started')}: {formatDate(stepExec.started_at)}
                            {stepExec.completed_at && ` • ${t('tcoReferrals.detail.completed', 'Completed')}: ${formatDate(stepExec.completed_at)}`}
                            {stepExec.duration_seconds != null && ` (${Math.round(stepExec.duration_seconds / 60)}m)`}
                          </p>
                        )}
                        {stepExec?.error_message && (
                          <p className="text-xs text-red-600 mt-1">{stepExec.error_message}</p>
                        )}
                        {stepExec?.decision_made && (
                          <p className="text-xs text-gray-600 mt-1">
                            Decision: {stepExec.decision_made}
                            {stepExec.decision_reason && ` — ${stepExec.decision_reason}`}
                          </p>
                        )}
                        {stepExec?.form_template_id && (
                          <div className="flex items-center gap-2 mt-1">
                            <Badge
                              variant="outline"
                              className={`text-xs ${
                                stepExec.form_completed
                                  ? 'text-green-600 border-green-300 bg-green-50'
                                  : 'text-amber-600 border-amber-300 bg-amber-50'
                              }`}
                            >
                              {stepExec.form_completed
                                ? `Form ${stepExec.form_status || 'completed'}`
                                : `Form ${stepExec.form_status || 'pending'}`
                              }
                            </Badge>
                            {stepExec.incentive_awarded && stepExec.incentive_amount && (
                              <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                                +${stepExec.incentive_amount.toFixed(2)} incentive
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                      {/* Step Action Buttons */}
                      {(canStartStep || canCompleteStep || canSkipStep) && (
                        <div className="flex-shrink-0 flex gap-1">
                          {canStartStep && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 text-xs"
                              onClick={() => openStepAction(stepNumber, 'start')}
                            >
                              <Play className="w-3 h-3 mr-1" />
                              {t('tcoReferrals.detail.stepStart', 'Start')}
                            </Button>
                          )}
                          {canCompleteStep && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 text-xs text-green-600 border-green-300 hover:bg-green-50"
                              onClick={() => openStepAction(stepNumber, 'complete')}
                            >
                              <CheckCircle className="w-3 h-3 mr-1" />
                              {t('tcoReferrals.detail.stepComplete', 'Complete')}
                            </Button>
                          )}
                          {canSkipStep && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-xs text-gray-500"
                              onClick={() => openStepAction(stepNumber, 'skip')}
                            >
                              <SkipForward className="w-3 h-3 mr-1" />
                              {t('tcoReferrals.detail.stepSkip', 'Skip')}
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                {(!template?.workflow_steps || template.workflow_steps.length === 0) && (
                  <div className="text-center py-6 text-gray-500 text-sm">
                    {canStartWorkflow
                      ? t('tcoReferrals.detail.startToSeeSteps', 'Start the workflow to see steps.')
                      : `${t('tcoReferrals.detail.step', 'Step')} ${instance.current_step} ${t('tcoReferrals.detail.of', 'of')} ${instance.total_steps}`
                    }
                    {instance.current_step_name && ` — ${instance.current_step_name}`}
                  </div>
                )}
              </div>

              {/* Current status summary */}
              <div className="mt-4 pt-4 border-t">
                <div className="flex flex-wrap gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">{t('tcoReferrals.detail.outreachAttempts', 'Outreach Attempts')}:</span>{' '}
                    <span className="font-medium">{instance.outreach_attempts}</span>
                  </div>
                  {instance.last_outreach_at && (
                    <div>
                      <span className="text-gray-500">{t('tcoReferrals.detail.lastOutreach', 'Last Outreach')}:</span>{' '}
                      <span className="font-medium">{formatDate(instance.last_outreach_at)}</span>
                    </div>
                  )}
                  {instance.member_response && (
                    <div>
                      <span className="text-gray-500">{t('tcoReferrals.detail.lastResponse', 'Last Response')}:</span>{' '}
                      <Badge variant="outline" className="ml-1">{instance.member_response}</Badge>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Outcome (if completed) */}
          {instance.outcome && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  {t('tcoReferrals.detail.outcome', 'Outcome')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Badge className={getOutcomeColor(instance.outcome)}>
                  {instance.outcome.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                </Badge>
              </CardContent>
            </Card>
          )}

          {/* Linked Referral */}
          {instance.service_referral_id && (
            <Card className="border-green-200 bg-green-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2 text-green-700">
                  <FileText className="w-4 h-4" />
                  {t('tcoReferrals.detail.linkedReferral', 'Linked Referral')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{t('tcoReferrals.detail.referralNumber', 'Referral')} #{instance.referral_number || instance.service_referral_id}</p>
                    <p className="text-sm text-gray-600">{t('tcoReferrals.detail.createdFromWorkflow', 'Created from this workflow')}</p>
                  </div>
                  {onViewReferral && (
                    <Button variant="outline" size="sm" onClick={() => onViewReferral(instance.service_referral_id!)}>
                      {t('tcoReferrals.detail.viewReferral', 'View Referral')}
                      <ExternalLink className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Status Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t('tcoReferrals.detail.status', 'Status')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">{t('tcoReferrals.detail.currentStatus', 'Current Status')}</span>
                <Badge className={getStatusColor(instance.status)}>
                  {instance.status.replace(/_/g, ' ')}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">{t('tcoReferrals.detail.priorityLabel', 'Priority')}</span>
                <Badge className={getPriorityColor(instance.priority)}>
                  {getPriorityLabel(instance.priority)}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">{t('tcoReferrals.detail.trigger', 'Trigger')}</span>
                <div className="flex items-center gap-1">
                  {getTriggerIcon(instance.trigger_type)}
                  <span className="text-sm">{instance.trigger_type.replace(/_/g, ' ')}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Member Info */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <User className="w-4 h-4" />
                {t('tcoReferrals.detail.member', 'Member')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-medium">{instance.member_name || `Member #${instance.member_id}`}</p>
              {instance.enrollment_id && (
                <p className="text-sm text-gray-500">{t('tcoReferrals.detail.enrollment', 'Enrollment')} #{instance.enrollment_id}</p>
              )}
            </CardContent>
          </Card>

          {/* Assignment */}
          {instance.assigned_to_name && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <UserCheck className="w-4 h-4" />
                  {t('tcoReferrals.detail.assignedTo', 'Assigned To')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-medium">{instance.assigned_to_name}</p>
              </CardContent>
            </Card>
          )}

          {/* Timeline */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {t('tcoReferrals.detail.timeline', 'Timeline')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">{t('tcoReferrals.detail.created', 'Created')}</span>
                <span>{formatDate(instance.created_at)}</span>
              </div>
              {instance.updated_at && (
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('tcoReferrals.detail.updated', 'Updated')}</span>
                  <span>{formatDate(instance.updated_at)}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Cancel Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('tcoReferrals.detail.dialogs.cancelTitle', 'Cancel Workflow')}</DialogTitle>
            <DialogDescription>
              {t('tcoReferrals.detail.dialogs.cancelDesc', 'Are you sure you want to cancel this workflow? This action cannot be undone.')}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="cancel-reason">{t('tcoReferrals.detail.dialogs.reason', 'Reason (optional)')}</Label>
            <Textarea
              id="cancel-reason"
              placeholder={t('tcoReferrals.detail.dialogs.enterReason', 'Enter reason for cancellation...')}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              className="mt-1"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelDialog(false)} disabled={actionLoading}>
              {t('tcoReferrals.detail.dialogs.keepWorkflow', 'Keep Workflow')}
            </Button>
            <Button variant="destructive" onClick={handleCancel} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
              {t('tcoReferrals.detail.dialogs.cancelWorkflow', 'Cancel Workflow')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Response Dialog */}
      <Dialog open={showRecordResponseDialog} onOpenChange={setShowRecordResponseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('tcoReferrals.detail.dialogs.recordResponseTitle', 'Record Member Response')}</DialogTitle>
            <DialogDescription>
              {t('tcoReferrals.detail.dialogs.recordResponseDesc', "Record the member's response to the outreach attempt.")}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>{t('tcoReferrals.detail.dialogs.responseType', 'Response Type')}</Label>
              <Select value={responseType} onValueChange={setResponseType}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="positive">{t('tcoReferrals.detail.dialogs.response.positive', 'Positive - Member engaged')}</SelectItem>
                  <SelectItem value="interested">{t('tcoReferrals.detail.dialogs.response.interested', 'Interested - Wants more info')}</SelectItem>
                  <SelectItem value="declined">{t('tcoReferrals.detail.dialogs.response.declined', 'Declined - Not interested')}</SelectItem>
                  <SelectItem value="no_answer">{t('tcoReferrals.detail.dialogs.response.noAnswer', 'No Answer - Could not reach')}</SelectItem>
                  <SelectItem value="callback">{t('tcoReferrals.detail.dialogs.response.callback', 'Callback - Requested callback')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="response-content">{t('tcoReferrals.detail.dialogs.responseDetails', 'Response Details (optional)')}</Label>
              <Textarea
                id="response-content"
                placeholder={t('tcoReferrals.detail.dialogs.enterResponseDetails', 'Enter any additional details about the response...')}
                value={responseContent}
                onChange={(e) => setResponseContent(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRecordResponseDialog(false)} disabled={actionLoading}>
              {t('tcoReferrals.detail.dialogs.cancelBtn', 'Cancel')}
            </Button>
            <Button onClick={handleRecordResponse} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {t('tcoReferrals.detail.dialogs.recordResponseBtn', 'Record Response')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Step Action Dialog */}
      <Dialog open={showStepActionDialog} onOpenChange={setShowStepActionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedStepAction === 'start' && t('tcoReferrals.detail.dialogs.startStepTitle', 'Start Step')}
              {selectedStepAction === 'complete' && t('tcoReferrals.detail.dialogs.completeStepTitle', 'Complete Step')}
              {selectedStepAction === 'skip' && t('tcoReferrals.detail.dialogs.skipStepTitle', 'Skip Step')}
              {selectedStepAction === 'fail' && t('tcoReferrals.detail.dialogs.failStepTitle', 'Mark Step as Failed')}
              {' '}#{selectedStepNumber}
            </DialogTitle>
            <DialogDescription>
              {selectedStepAction === 'start' && t('tcoReferrals.detail.dialogs.startStepDesc', 'Begin executing this workflow step.')}
              {selectedStepAction === 'complete' && t('tcoReferrals.detail.dialogs.completeStepDesc', 'Mark this step as successfully completed.')}
              {selectedStepAction === 'skip' && t('tcoReferrals.detail.dialogs.skipStepDesc', 'Skip this step. Please provide a reason.')}
              {selectedStepAction === 'fail' && t('tcoReferrals.detail.dialogs.failStepDesc', 'Mark this step as failed. Please provide details.')}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="step-action-notes">
              {selectedStepAction === 'skip'
                ? t('tcoReferrals.detail.dialogs.skipReason', 'Reason for skipping')
                : selectedStepAction === 'fail'
                ? t('tcoReferrals.detail.dialogs.failureDetails', 'Failure details')
                : t('tcoReferrals.detail.dialogs.notes', 'Notes (optional)')
              }
            </Label>
            <Textarea
              id="step-action-notes"
              placeholder={t('tcoReferrals.detail.dialogs.enterStepNotes', 'Enter notes...')}
              value={stepActionNotes}
              onChange={(e) => setStepActionNotes(e.target.value)}
              className="mt-1"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStepActionDialog(false)} disabled={actionLoading}>
              {t('tcoReferrals.detail.dialogs.cancelBtn', 'Cancel')}
            </Button>
            <Button
              onClick={handleStepAction}
              disabled={actionLoading || ((['skip', 'fail'].includes(selectedStepAction)) && !stepActionNotes.trim())}
              variant={selectedStepAction === 'fail' ? 'destructive' : 'default'}
            >
              {actionLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : selectedStepAction === 'start' ? (
                <Play className="w-4 h-4 mr-2" />
              ) : selectedStepAction === 'complete' ? (
                <CheckCircle className="w-4 h-4 mr-2" />
              ) : selectedStepAction === 'skip' ? (
                <SkipForward className="w-4 h-4 mr-2" />
              ) : (
                <AlertTriangle className="w-4 h-4 mr-2" />
              )}
              {selectedStepAction === 'start' && t('tcoReferrals.detail.dialogs.startBtn', 'Start Step')}
              {selectedStepAction === 'complete' && t('tcoReferrals.detail.dialogs.completeBtn', 'Complete Step')}
              {selectedStepAction === 'skip' && t('tcoReferrals.detail.dialogs.skipBtn', 'Skip Step')}
              {selectedStepAction === 'fail' && t('tcoReferrals.detail.dialogs.failBtn', 'Mark Failed')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Referral Dialog */}
      <Dialog open={showCreateReferralDialog} onOpenChange={setShowCreateReferralDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('tcoReferrals.detail.dialogs.createReferralTitle', 'Create Referral from Workflow')}</DialogTitle>
            <DialogDescription>
              {t('tcoReferrals.detail.dialogs.createReferralDesc', 'Create a service referral based on this workflow for {{name}}.', { name: instance.member_name || `Member #${instance.member_id}` })}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>{t('tcoReferrals.detail.dialogs.serviceTypes', 'Service Types')}</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {['food_assistance', 'housing_support', 'transportation', 'healthcare', 'employment'].map((type) => (
                  <Button
                    key={type}
                    variant={referralServiceTypes.includes(type) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      setReferralServiceTypes((prev) =>
                        prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
                      );
                    }}
                  >
                    {type.replace(/_/g, ' ')}
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <Label>{t('tcoReferrals.detail.dialogs.urgencyLabel', 'Urgency')}</Label>
              <Select value={referralUrgency} onValueChange={setReferralUrgency}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="routine">{t('tcoReferrals.detail.dialogs.urgency.routine', 'Routine')}</SelectItem>
                  <SelectItem value="urgent">{t('tcoReferrals.detail.dialogs.urgency.urgent', 'Urgent')}</SelectItem>
                  <SelectItem value="emergent">{t('tcoReferrals.detail.dialogs.urgency.emergent', 'Emergent')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="referral-notes">{t('tcoReferrals.detail.dialogs.notes', 'Notes (optional)')}</Label>
              <Textarea
                id="referral-notes"
                placeholder={t('tcoReferrals.detail.dialogs.enterNotes', 'Enter any notes for the referral...')}
                value={referralNotes}
                onChange={(e) => setReferralNotes(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateReferralDialog(false)} disabled={actionLoading}>
              {t('tcoReferrals.detail.dialogs.cancelBtn', 'Cancel')}
            </Button>
            <Button onClick={handleCreateReferral} disabled={actionLoading || referralServiceTypes.length === 0}>
              {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              {t('tcoReferrals.detail.dialogs.createReferralBtn', 'Create Referral')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
